import * as React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { RootState } from "store";
import { AxiosError } from "axios";
import SoftTypography from "components/SoftTypography";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import dayjs from "dayjs";
import {
  waitForSubscriptionPaymentStatus,
  SUBSCRIPTION_PAYMENT_PENDING_MESSAGE,
} from "utils/waitForSubscriptionPaymentStatus";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- TYPE ALIASES ----------

type DateInput = string | number | Date;

// ---------- INTERFACES ----------

interface Divisions {
  division_id: number;
  division_name: string;
}

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

interface SubscriptionTypeOption {
  subscription_type_id: number;
  subscription_type_name: string;
}

interface TableMetaData {
  currentTableData: Array<{ index: number }>;
  rowIndex: number;
  tableData: any[];
}

interface RenewalInfo {
  subscription_id: number;
  asset_id: number;
  asset_name: string;
  asset_service_id?: string;
  subscription_type_id: number;
  subscription_type_name: string;
  amount: number | null;
  duration_in_days: number | null;
  currency?: string;
  last_start_date?: string;
  last_end_date?: string;
  suggested_start_date: string;
  suggested_end_date: string | null;
}

// ---------- EXTRACTED COMPONENTS ----------

interface AssetNameCellProps {
  assetName: string;
}

const AssetNameCell: React.FC<AssetNameCellProps> = ({ assetName }) => {
  const displayValue =
    assetName?.length > 20 ? `${assetName.substring(0, 20)}...` : assetName;
  return (
    <Tooltip title={assetName}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface StatusCellProps {
  value: string;
}

const StatusCell: React.FC<StatusCellProps> = ({ value }) => (
  <span
    className="active-btn"
    style={{
      borderColor: value ? "var(--primary-color, #2e7d32)" : "#fe346e",
    }}
  >
    {value ? "Active" : "Inactive"}
  </span>
);

interface ActionsCellProps {
  subscriptionId: number;
  canRenew: boolean;
  renewing: boolean;
  onRenew: (subscriptionId: number) => void;
  onView: (subscriptionId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  subscriptionId,
  canRenew,
  renewing,
  onRenew,
  onView,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    {canRenew && (
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={() => onRenew(subscriptionId)}
        disabled={renewing}
      >
        {renewing ? <CircularProgress size={16} color="inherit" /> : "Renew"}
      </Button>
    )}
    <IconButton aria-label="view" onClick={() => onView(subscriptionId)}>
      <VisibilityIcon />
    </IconButton>
  </Box>
);

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

const CustomerSubscriptionList = () => {
  const navigate = useNavigate();
  const [divisionsFilter, setDivisionsFilter] = React.useState<number | null>(
    null
  );
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<AssetTypeOption | null>(null);
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = React.useState<SubscriptionTypeOption | null>(null);
  const [divisions, setDivisions] = React.useState<Divisions[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeOption[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = React.useState<SubscriptionTypeOption[]>([]);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [renewalLoadingId, setRenewalLoadingId] = React.useState<number | null>(
    null
  );
  const [renewalInfo, setRenewalInfo] = React.useState<RenewalInfo | null>(null);
  const [renewalDialogOpen, setRenewalDialogOpen] = React.useState(false);
  const [paymentLoading, setPaymentLoading] = React.useState(false);
  const [startDateInput, setStartDateInput] = React.useState("");
  const [endDateInput, setEndDateInput] = React.useState("");
  const [startDateError, setStartDateError] = React.useState<string | null>(null);
  const [endDateError, setEndDateError] = React.useState<string | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);
  const appCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";
  const { fetchError, fetchSuccess } = useSnackbar();

  const formatDate = (dateString: DateInput) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  /** Renew only for lapsed subs — not scheduled future rows (inactive but end_date still in the future). */
  const canShowRenewal = (row: {
    is_active?: unknown;
    end_date?: DateInput | null;
  }) => {
    if (!!row?.is_active) return false;
    const end = row?.end_date;
    if (end == null || end === "") return false;
    return dayjs(end).startOf("day").isBefore(dayjs().startOf("day"));
  };

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`/divisions/customer/${user?.userId}`);
      setDivisions(response?.data?.divisions);
    } catch (err) {
      console.error("Error fetching statuses:", err);
    }
  };

  const fetchAssetTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const list = res.data?.list ?? [];
      setAssetTypes(
        list.map((a: AssetTypeOption) => ({
          asset_type_id: a.asset_type_id,
          asset_type_name: a.asset_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch asset types", e);
    }
  }, []);

  const fetchSubscriptionTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/subscription-types");
      const list = res.data?.list ?? res.data ?? [];
      setSubscriptionTypes(
        list.map((s: SubscriptionTypeOption) => ({
          subscription_type_id: s.subscription_type_id,
          subscription_type_name: s.subscription_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch subscription types", e);
    }
  }, []);

  React.useEffect(() => {
    fetchDivisions();
  }, []);

  React.useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  React.useEffect(() => {
    fetchSubscriptionTypes();
  }, [fetchSubscriptionTypes]);

  const handleViewSubscription = (subscriptionId: number) => {
    navigate(`/view-subscription/${subscriptionId}`);
  };

  // Render functions for table columns
  const renderAssetNameCell = (value: string, tableMeta: TableMetaData) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    const assetName = data?.asset_name;
    return <AssetNameCell assetName={assetName} />;
  };

  const renderStartDate = (value: DateInput) => {
    return formatDate(value);
  };

  const renderEndDate = (value: DateInput) => {
    return formatDate(value);
  };

  const renderStatusCell = (value: string) => {
    return <StatusCell value={value} />;
  };

  const renderActionsCell = (value: any, tableMeta: TableMetaData) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    const subscriptionId = data?.subscription_id;
    const renewing =
      renewalLoadingId === subscriptionId ||
      (paymentLoading && renewalInfo?.subscription_id === subscriptionId);

    return (
      <ActionsCell
        subscriptionId={subscriptionId}
        onView={handleViewSubscription}
        canRenew={canShowRenewal(data)}
        onRenew={handleRenewSubscription}
        renewing={Boolean(renewing)}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "subscription_type_name", label: "Subscription Type" },
    ...(user?.type === "company" && user?.division_id === null
      ? [{ name: "division_name", label: "Division" }]
      : []),
    {
      name: "asset_name",
      label: "Brand Name",
      options: {
        filter: false,
        customBodyRender: renderAssetNameCell,
      },
    },

    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "start_date",
      label: "Start Date",
      options: {
        filter: false,
        customBodyRender: renderStartDate,
      },
    },
    {
      name: "end_date",
      label: "End Date",
      options: {
        filter: false,
        customBodyRender: renderEndDate,
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusCell,
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  const fetchData = React.useMemo(
    () => async () => {
      try {
        const response = await axios.get(
          `/subscription/customer/${user?.userId}`
        );
        let subscriptions = response?.data?.list;
        if (divisionsFilter) {
          subscriptions = subscriptions.filter(
            (s: { division_id: number }) => s.division_id === divisionsFilter
          );
        }
        if (assetTypeFilter) {
          subscriptions = subscriptions.filter(
            (s: { asset_type_id: number }) => s.asset_type_id === assetTypeFilter.asset_type_id
          );
        }
        if (subscriptionTypeFilter) {
          subscriptions = subscriptions.filter(
            (s: { subscription_type_id: number }) =>
              s.subscription_type_id === subscriptionTypeFilter.subscription_type_id
          );
        }
        setRows(subscriptions);
      } catch (err) {
        if (err instanceof AxiosError && err) {
          setError(err);
        }
      }
    },
    [user, divisionsFilter, assetTypeFilter, subscriptionTypeFilter]
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadRazorpayScript = React.useCallback((): Promise<void> => {
    if (typeof window !== "undefined" && (window as any).Razorpay) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });
  }, []);

  const computeEndDate = React.useCallback(
    (start: string, duration?: number | null) => {
      if (!start || duration == null) return "";
      const days = Number(duration);
      if (!Number.isFinite(days) || days <= 0) return "";
      const parsed = dayjs(start, "YYYY-MM-DD", true);
      if (!parsed.isValid()) return "";
      return parsed.add(days - 1, "day").format("YYYY-MM-DD");
    },
    []
  );

  const formatCurrency = React.useCallback(
    (amount?: number | null, currency = String(appCurrency || "USD")) => {
      if (amount == null) return "-";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency,
      }).format(amount);
    },
    [appCurrency]
  );

  const resetRenewalState = React.useCallback(() => {
    setRenewalDialogOpen(false);
    setRenewalInfo(null);
    setStartDateInput("");
    setEndDateInput("");
    setStartDateError(null);
    setEndDateError(null);
  }, []);

  const closeRenewalDialog = React.useCallback(() => {
    if (paymentLoading) return;
    resetRenewalState();
  }, [paymentLoading, resetRenewalState]);

  React.useEffect(() => {
    if (!renewalDialogOpen || !renewalInfo) return;

    const durationDays =
      renewalInfo.duration_in_days != null
        ? Number(renewalInfo.duration_in_days)
        : null;

    if (!startDateInput) {
      setEndDateInput("");
      setEndDateError("Select a start date to calculate the end date.");
      return;
    }

    if (!durationDays || !Number.isFinite(durationDays) || durationDays <= 0) {
      setEndDateInput("");
      setEndDateError("Plan duration missing. Please contact support.");
      return;
    }

    const computed = computeEndDate(startDateInput, durationDays);
    if (computed) {
      setEndDateInput(computed);
      setEndDateError(null);
    } else {
      setEndDateInput("");
      setEndDateError("Unable to calculate end date. Check the start date.");
    }
  }, [startDateInput, renewalInfo, renewalDialogOpen, computeEndDate]);

  const handleRenewSubscription = React.useCallback(
    async (subscriptionId: number) => {
      if (!subscriptionId) return;
      setRenewalLoadingId(subscriptionId);
      try {
        const { data } = await axios.get<RenewalInfo>(
          `/customer-subscriptions/renewal-info/${subscriptionId}`
        );
        const normalizedData = {
          ...data,
          amount:
            data.amount != null && !Number.isNaN(Number(data.amount))
              ? Number(data.amount)
              : null,
          duration_in_days:
            data.duration_in_days != null &&
            !Number.isNaN(Number(data.duration_in_days))
              ? Number(data.duration_in_days)
              : null,
        };
        setRenewalInfo(normalizedData);
        const fallbackStart = normalizedData.suggested_start_date ?? dayjs().format("YYYY-MM-DD");
        setStartDateInput(fallbackStart);
        const initialEnd =
          computeEndDate(fallbackStart, normalizedData.duration_in_days) ||
          normalizedData.suggested_end_date ||
          "";
        setEndDateInput(initialEnd);
        setStartDateError(null);
        setEndDateError(null);
        setRenewalDialogOpen(true);
      } catch (err: any) {
        fetchError(
          err?.response?.data?.error ||
            "Unable to load renewal details for this subscription."
        );
      } finally {
        setRenewalLoadingId(null);
      }
    },
    [fetchError, computeEndDate]
  );

  const handleRenewalPayment = React.useCallback(async () => {
    if (!renewalInfo) {
      fetchError("Please select a subscription to renew.");
      return;
    }

    if (!startDateInput) {
      setStartDateError("Start date is required.");
      return;
    }
    const parsedStart = dayjs(startDateInput, "YYYY-MM-DD", true);
    if (!parsedStart.isValid()) {
      setStartDateError("Start date must be in YYYY-MM-DD format.");
      return;
    }

    if (!endDateInput) {
      setEndDateError("End date could not be calculated. Please adjust the start date.");
      return;
    }
    const parsedEnd = dayjs(endDateInput, "YYYY-MM-DD", true);
    if (!parsedEnd.isValid()) {
      setEndDateError("Calculated end date is invalid. Please adjust the start date.");
      return;
    }
    if (parsedEnd.isBefore(parsedStart)) {
      setEndDateError("Calculated end date cannot be before start date.");
      return;
    }

    try {
      setPaymentLoading(true);
      const { data } = await axios.post(
        "/customer-subscriptions/create-order",
        {
          subscription_id: renewalInfo.subscription_id,
        }
      );

      console.log("[PAYMENT_DEBUG] customer (renewal list): create-order OK", {
        orderId: data.orderId,
      });

      await loadRazorpayScript();
      const Razorpay = (window as any).Razorpay;
      if (!Razorpay) {
        throw new Error("Payment gateway could not be loaded.");
      }

      const rzp = new Razorpay({
        key: data.key_id,
        amount: Number(data.amount),
        currency: data.currency || String(appCurrency || "USD"),
        order_id: data.orderId,
        name: "Scout Track Solutions",
        description: `${renewalInfo.subscription_type_name} renewal`,
        handler: async (response: {
          razorpay_payment_id: string;
          razorpay_order_id: string;
          razorpay_signature: string;
        }) => {
          try {
            const verifyPayload = {
              subscription_id: renewalInfo.subscription_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              start_date: startDateInput,
              end_date: endDateInput || undefined,
            };
            const verifyPromise = axios.post("/customer-subscriptions/verify", verifyPayload);
            const poll = await waitForSubscriptionPaymentStatus(
              response.razorpay_order_id,
              "customer"
            );
            let verifyOk = false;
            try {
              const verifyRes = await verifyPromise;
              verifyOk = verifyRes.status === 200 || verifyRes.status === 201;
            } catch (e) {
              verifyOk = false;
            }
            if (poll.activated || verifyOk) {
              fetchSuccess("Subscription renewed successfully.");
            } else {
              fetchSuccess(SUBSCRIPTION_PAYMENT_PENDING_MESSAGE);
            }
            resetRenewalState();
            await fetchData();
          } catch (verifyError: any) {
            fetchError(
              verifyError?.response?.data?.error ||
                "Payment verification failed."
            );
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
          },
        },
      });
      rzp.open();
    } catch (err: any) {
      fetchError(
        err?.response?.data?.error ||
          err?.message ||
          "Unable to initiate payment."
      );
      setPaymentLoading(false);
    }
  }, [
    renewalInfo,
    startDateInput,
    endDateInput,
    fetchError,
    fetchSuccess,
    loadRazorpayScript,
    fetchData,
    resetRenewalState,
  ]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mb: 2 }}>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Asset Type
            </SoftTypography>
            <Autocomplete
              options={assetTypes}
              getOptionLabel={(o) => o.asset_type_name}
              value={assetTypeFilter}
              onChange={(_, v) => setAssetTypeFilter(v)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="All" />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Subscription Type
            </SoftTypography>
            <Autocomplete
              options={subscriptionTypes}
              getOptionLabel={(o) => o.subscription_type_name}
              value={subscriptionTypeFilter}
              onChange={(_, v) => setSubscriptionTypeFilter(v)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="All" />
              )}
            />
          </FormControl>
          {user?.type === "company" && user?.division_id === null && (
            <FormControl sx={{ width: 200, minWidth: 200 }}>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                Divisions
              </SoftTypography>
              <Autocomplete
                options={divisions}
                getOptionLabel={(option) => option.division_name}
                value={divisions?.find((d) => d.division_id === divisionsFilter) ?? null}
                onChange={(event, value) => {
                  setDivisionsFilter(value?.division_id ?? null);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" placeholder="All" />
                )}
              />
            </FormControl>
          )}
        </Box>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-assets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Subscriptions"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
        <Dialog
          open={renewalDialogOpen}
          onClose={closeRenewalDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Renew Subscription</DialogTitle>
          <DialogContent dividers>
            {renewalInfo ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Asset:
                  </Box>{" "}
                  {renewalInfo.asset_name || "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Subscription:
                  </Box>{" "}
                  {renewalInfo.subscription_type_name || "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Amount:
                  </Box>{" "}
                  {formatCurrency(renewalInfo.amount, renewalInfo.currency)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column" }}>
                    <Typography variant="caption" fontWeight={600} gutterBottom>
                      Start Date
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={startDateInput}
                      onChange={(e) => {
                        setStartDateInput(e.target.value);
                        setStartDateError(null);
                      }}
                      error={Boolean(startDateError)}
                      helperText={startDateError || "Pick the new activation date."}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column" }}>
                    <Typography variant="caption" fontWeight={600} gutterBottom>
                      End Date
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={endDateInput}
                      InputProps={{ readOnly: true }}
                      error={Boolean(endDateError)}
                      helperText={
                        endDateError || "Auto-calculated from plan duration."
                      }
                    />
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Suggested Start:
                  </Box>{" "}
                  {renewalInfo.suggested_start_date
                    ? formatDate(renewalInfo.suggested_start_date)
                    : "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Suggested End:
                  </Box>{" "}
                  {renewalInfo.suggested_end_date
                    ? formatDate(renewalInfo.suggested_end_date)
                    : "-"}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Previous Period:
                  </Box>{" "}
                  {renewalInfo.last_start_date
                    ? `${formatDate(renewalInfo.last_start_date)}`
                    : "-"}{" "}
                  {renewalInfo.last_end_date
                    ? `to ${formatDate(renewalInfo.last_end_date)}`
                    : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  These dates will be sent to Razorpay automatically. Complete
                  the payment to activate the renewal instantly.
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2">
                Loading renewal details...
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRenewalDialog} disabled={paymentLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRenewalPayment}
              disabled={
                paymentLoading ||
                !renewalInfo ||
                renewalInfo?.amount == null ||
                !startDateInput ||
                !endDateInput ||
                Boolean(startDateError) ||
                Boolean(endDateError)
              }
            >
              {paymentLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Pay with Razorpay"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </DashboardLayout>
  );
};

export default CustomerSubscriptionList;